import React, { useState } from "react";
import Webcam from "react-webcam";

/**
 * CameraWebcam component - Handles webcam rendering with filters and transformations
 * 
 * This component provides a wrapper around the react-webcam library with additional
 * functionality for camera controls, filters, and responsive aspect ratios.
 * 
 * Key Features:
 * - Responsive aspect ratio handling for different orientations
 * - Real-time CSS filter application for visual effects
 * - Front/back camera switching with proper re-rendering
 * - Flash/torch control for supported devices
 * - Mirror effect for front camera (natural user experience)
 * - Error handling for camera permissions and device issues
 * - High-quality PNG screenshot capture
 * - Privacy-focused (audio disabled by default)
 * 
 * @param {Object} props - Component props
 * @param {React.RefObject} props.webcamRef - Ref for webcam element to access camera methods like getScreenshot()
 * @param {string} props.facingMode - Camera facing mode ('user' for front camera, 'environment' for back camera)
 * @param {string} props.flashMode - Flash mode ('on' to enable flashlight, 'off' to disable)
 * @param {Function} props.handleCameraError - Error handler for camera permission/device issues
 * @param {string} props.ratio - Aspect ratio (e.g., "16:9", "4:3", "9:16") - affects container layout
 * @param {string} props.filter - CSS filter string for camera effects (e.g., "grayscale(100%)", "sepia(100%)")
 * @returns {JSX.Element} - Webcam component wrapper with responsive layout and real-time effects
 */
const CameraWebcam = ({
  webcamRef, // React ref to access webcam component methods
  facingMode, // Camera direction: 'user' (front-facing) or 'environment' (back-facing)
  flashMode, // Flash control: 'on' enables torch, 'off' disables
  handleCameraError, // Callback function for camera-related errors
  ratio, // Aspect ratio string like "16:9" - determines container shape
  filter, // CSS filter string for real-time visual effects
  controlsHeight
}) => {
  const [zoom, setZoom] = useState(1);

  return (
    // Container with responsive aspect ratio
    // Uses CSS aspect-ratio property for modern browsers, falls back to explicit height for 9:16
    <div
      className="ratio-wrapper"
      style={{ 
        aspectRatio: ratio.replace(":", "/"), // Convert "16:9" to "16/9" for CSS compatibility
        height: ratio === "9:16" ? '100vh' : null // Full viewport height for portrait mode on mobile devices
      }}
    >
      {/* 
        Webcam component with comprehensive configuration:
        - Key prop forces re-render when camera direction changes to prevent camera lag
        - Ref provides access to capture methods like getScreenshot()
        - Audio disabled for privacy and performance optimization
        - PNG format ensures high-quality captures with lossless compression
        - Video constraints control camera hardware features
        - Error handling manages permission denials and device compatibility
        - Styling applies real-time effects and proper display behavior
      */}
      <Webcam
        key={facingMode} // Force re-render when facing mode changes to prevent camera lag and ensure proper initialization
        ref={webcamRef} // Ref for accessing screenshot() and other webcam methods
        audio={false} // Disable audio capture for privacy protection and performance
        screenshotFormat="image/png" // Set image format for screenshots (PNG provides lossless quality)
        
        // Video constraints for camera hardware configuration
        videoConstraints={{
          facingMode, // Camera direction: 'user' (front) or 'environment' (back) - affects which camera is used
          torch: flashMode === "on", // Enable flashlight/torch if flash mode is 'on' - only works on supported devices
          advanced: [{ zoom }]
        }}
        
        // Error handling for camera permissions and device compatibility issues
        onUserMediaError={handleCameraError} // Callback for camera permission denied, device not found, etc.
        
        // Styling for proper display and real-time visual effects
        style={{
          width: "100%", // Full width of container for responsive layout
          height: "100%", // Full height of container to fill available space
          objectFit: "cover", // Cover entire area without distortion - crops to fit
          filter, // Apply CSS filters for real-time visual effects (grayscale, sepia, etc.)
          transform: facingMode === "user" ? "scaleX(-1)" : "none", // Mirror front camera horizontally for natural user experience
        }}
      />
      {console.log(zoom, "zoom")}
        
        {/* 🔥 ZOOM CONTROLS */}
        {/* <div className="zoom-controls" style={{bottom: controlsHeight + 10}}>
          <button onClick={() => setZoom((z) => Math.max(1, z - 1))}>
            -
          </button>
          
          <input
            type="range"
            min="1"
            max="3"
            step="1"
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
          
          <button onClick={() => setZoom((z) => Math.min(3, z + 1))}>
            +
          </button>
        </div> */}
      </div>
  );
};

export default CameraWebcam;
